import React from 'react';
import {
  MonoKicker,
  GoldRule,
  PosterHeadline,
  SerifStory,
  GoldClause,
  GoldCTA,
  CtaMicroline,
  AppStoreBadge,
  SectionMarker,
  Reveal,
  HeroStack,
  HERO_GLOBE,
} from '../index';

/**
 * §06 YOUR TURN — the spectacle bookend.
 *
 * The page opened on the void and the globe; it closes the same way, with the
 * number gone and only the invitation left. A reprise, not a repeat — the
 * globe returns dimmed and smaller, so the visitor feels they have come back
 * to where they started, quieter.
 *
 * Reuses hero-globe.webp deliberately: it is already in cache from the hero,
 * so this band costs zero additional network. Never introduce a second globe
 * asset here.
 *
 * Both the headline and the serif line are canonical copy-library lines,
 * verbatim. "What it costs to look · nothing" is verified — the app is free to
 * download. Do not upgrade it to "free forever".
 *
 * This is the fifth and last CTA surface.
 */
export function FinalCTA() {
  return (
    <section
      id="final-cta"
      aria-labelledby="final-h"
      className="band-nebula-void relative isolate overflow-x-clip"
      style={{ minHeight: '70vh' }}
    >
      <style>{`
        @supports (height: 70svh) { #final-cta { min-height: 70svh; } }
      `}</style>

      {/* Dimmed reprise of the hero globe.
          This band inherits the hero's construction and therefore inherited
          its defect: measured at 390×740 the kicker sat at 3.65:1 against the
          globe's CHICAGO label showing through. Same lever as the hero — the
          type moves out from under the spectacle rather than the spectacle
          being drowned: a shorter band, the crop lifted, and the scrim ramped
          to solid before the type starts. Width capped for the same reason as
          the hero: the source is a 2× upscale of a 445px crop. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[26%] overflow-hidden opacity-55 lg:inset-y-0 lg:left-auto lg:right-[-40px] lg:h-full lg:w-[min(520px,40vw)]"
      >
        <img
          src={HERO_GLOBE.src}
          alt=""
          width={HERO_GLOBE.width}
          height={HERO_GLOBE.height}
          loading="lazy"
          decoding="async"
          className="hero-globe-img block h-full w-full object-cover object-[50%_4%] lg:object-[40%_50%]"
        />
      </div>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-[1] lg:hidden"
        style={{
          background:
            'linear-gradient(to bottom, rgba(5,5,14,0) 0%, rgba(5,5,14,0.30) 8%, rgba(5,5,14,0.80) 14%, #05050E 20%)',
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-[1] hidden lg:block"
        style={{
          background:
            'linear-gradient(to left, transparent 0%, rgba(5,5,14,0.40) 30%, rgba(5,5,14,0.88) 50%, #05050E 66%)',
        }}
      />

      <div className="relative z-10 mx-auto flex min-h-[inherit] w-full max-w-[1200px] flex-col justify-end px-5 pb-20 pt-24 md:px-10 xl:px-16 lg:justify-center lg:pb-24">
        <HeroStack className="w-full max-w-[560px] lg:max-w-[520px]">
          <SectionMarker className="mb-6">§ 06 · Your turn</SectionMarker>

          <Reveal>
            <GoldRule />
            <MonoKicker className="mt-2">TWO STRANGERS · ONE MAP</MonoKicker>
            <PosterHeadline
              as="h2"
              id="final-h"
              size="hero"
              className="mt-3.5"
              lines={[{ text: 'Your story' }, { text: 'awaits', outline: true }]}
            />
          </Reveal>

          <Reveal delay={120}>
            <SerifStory className="mt-[18px] w-full max-w-[42ch]">
              Discover the moments that <GoldClause>brought you together.</GoldClause>
            </SerifStory>
          </Reveal>

          <Reveal delay={200} className="mt-6 w-full">
            <div className="flex flex-col items-start gap-1 pb-[10px] min-[421px]:flex-row min-[421px]:items-baseline">
              <span className="cp-mono text-[length:var(--fs-mono)] whitespace-nowrap">
                What it costs to look
              </span>
              <span
                aria-hidden="true"
                className="mx-3 hidden min-w-[24px] flex-1 -translate-y-[0.28em] border-b border-dotted border-[color:var(--cp-stroke-muted)] min-[421px]:block"
              />
              <span className="cp-mono text-[length:var(--fs-mono-value)] text-gold-400">
                Nothing
              </span>
            </div>
            <span aria-hidden="true" className="block h-px w-full bg-[color:var(--cp-hairline)]" />
          </Reveal>

          <Reveal delay={280} className="mt-8 flex w-full flex-col items-start gap-4">
            <div className="flex w-full flex-col items-stretch gap-4 min-[560px]:w-auto min-[560px]:flex-row min-[560px]:items-center">
              <GoldCTA data-testid="final-download" fullWidth>
                Discover Your Crossed Paths
              </GoldCTA>
              <AppStoreBadge data-testid="final-badge" />
            </div>
            <CtaMicroline className="w-full" />
          </Reveal>
        </HeroStack>
      </div>
    </section>
  );
}

export default FinalCTA;
